import { Button } from '@eb/eds-button';
import { Icon } from '@eb/eds-icon';
import { AlertChunky, LogoEBrand } from '@eb/eds-iconography';
import { ModalBody } from '@eb/eds-modal';
import { getFormattedEventDate } from '@eb/event-date-time';
import { gettext } from '@eb/i18n';
import React from 'react';
import '../../common.scss';
import { CTA_ACTION } from '../../constants';
import { EventbriteEvent } from '../../models';
import { CouldntFindThatIllustration } from '../../resources/couldntFindThatIllustration';
import './errorsPage.scss';

const infoBoxText = (locale: string) =>
    gettext(
        'If you are organizing this event, visit our <a target=_blank href=%(href)s>troubleshooting guide</a>',
        {
            href: `/support/articleredirect?anum=000041635&lg=${locale}`,
        },
    );

const invalidRequestMessage = (
    onCTAClick: (action: CTA_ACTION) => void,
    locale: string,
    name: string,
    date: string,
) => (
    <div className="errors-page-modal-container">
        <ModalBody title={name} secondaryTitle={date}>
            <div className="content-centered">
                <div className="errors-page-cta">
                    <div>
                        <div className="eds-l-mar-top-4 eds-l-mar-bot-12 errors-page-brand-icon">
                            <Icon type={<LogoEBrand />} size="small" />
                        </div>
                        <div className="eds-text-hs eds-l-mar-bot-6">
                            {gettext(
                                'Get tickets for this event on Eventbrite',
                            )}
                        </div>
                        <Button
                            __containerClassName="eds-align--center"
                            data-spec="error-go-to-checkout-btn"
                            onClick={() =>
                                onCTAClick(CTA_ACTION.GO_TO_LISTING_PAGE)
                            }
                            size="responsive"
                            style="fill"
                        >
                            {gettext('Get tickets')}
                        </Button>
                    </div>
                </div>

                <div className="errors-page-info-box eds-l-mar-bot-8 eds-l-mar-hor-8">
                    <Icon
                        type={<AlertChunky />}
                        color="grey-600"
                        size="small"
                    />
                    <span
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{
                            __html: infoBoxText(locale).toString(),
                        }}
                    />
                </div>
            </div>
        </ModalBody>
    </div>
);

const unavailableMessage = (
    <div className="content-centered" data-spec="error-unavailable-message">
        <CouldntFindThatIllustration />

        <div className="eds-text-hs eds-l-mar-bot-2">
            {gettext('Oops, this is not available right now')}
        </div>
    </div>
);

export interface ErrorsPageProps {
    isValidHTTPSRequest: boolean;
    onCTAClick: (action: CTA_ACTION) => void;
    env: {
        localeInfo: {
            locale: string;
        };
    };
    event?: EventbriteEvent;
}

export const ErrorsPage: React.FunctionComponent<ErrorsPageProps> = ({
    isValidHTTPSRequest,
    onCTAClick,
    env,
    event,
}) => {
    const locale = env.localeInfo.locale;
    const name = event?.name.text ?? '';
    const eventDate = event?.is_series_parent
        ? gettext('Multiple Dates').toString()
        : getFormattedEventDate(event, locale);
    const date = event ? eventDate : '';
    const content = !isValidHTTPSRequest
        ? invalidRequestMessage(onCTAClick, locale, name, date)
        : unavailableMessage;

    return <ModalBody>{content}</ModalBody>;
};
